import { errorHandler } from "../utils/errorHandler";
import axios from "../utils/axiosConfig";
import {
  setUsers,
  setUsersError,
  setUsersLoading,
  setImportUsersError,
  setImportUsersLoading,
  setImportUsersSuccess
} from "../redux/slices/manageUsers";
import { User, UsersResponse, ImportUsersResponse } from "../types/manageUsers";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

interface ProjectOption {
  value: string;
  label: string;
}

interface EpicOption {
  value: string;
  label: string;
  isDisabled?: boolean;
  isImported?: boolean;
  id?: number;
}

interface BoardOption {
  value: string;
  label: string;
}

interface ProjectsResponse {
  message: string;
  projects: Array<{
    project_name: string;
    project_id: string;
  }>;
}

// Add interface for status stages order
interface StatusStagesOrder {
  [projectName: string]: {
    [stateName: string]: string | number;
  };
}

interface EpicsResponse {
  message: string;
  boards: Array<{
    name: string;
    id: string;
  }>;
  epics: Array<{
    epic_name: string;
    epic_id: string;
    is_imported?: boolean;
    id?: number;
  }>;
  status_stages?: StatusStages;
  status_stages_order?: StatusStagesOrder;
  display_message?: string;
  processing_status_stages?: boolean;
}

interface StatusStages {
  [projectName: string]: StatusStageProject;
}

interface StatusStageItem {
  [key: string]: string;
}

interface StatusStageTeam {
  [stateName: string]: StatusStageItem;
}

interface StatusStageProject {
  [teamName: string]: StatusStageTeam;
}

interface ImportEpicResponse {
  message: string;
  epic_id: string;
  imported_items_count: number;
}

interface UpdateStagesRequest {
  status_stages: Record<string, Record<string, Record<string, string>>>;
}

interface UpdateStagesResponse {
  message: string;
}

interface JiraSettingsResponse {
  organization?: string;
  api_token?: string;
  project_name?: string;
  is_active?: boolean;
  status_stages?: string | Record<string, any>;
  status_stages_order?: string | Record<string, any>;
  processing_states?: boolean;
  processed_projects?: Record<string, { hidden: boolean }>;
  priorities?: Record<string, any>;
}

// Interface for manage projects API
interface ManageProjectsRequest {
  projects: Array<{
    name: string;
    hidden: boolean;
  }>;
}

interface ManageProjectsResponse {
  message: string;
}

const useJiraService = () => {
  const dispatch = useDispatch();

  const showError = (error: any) => {
    errorHandler.showError(error);
  };

  const isJiraAuthError = (error: any): boolean => {
    const detail = error?.response?.data?.detail || "";
    const message = error?.response?.data?.message || "";

    return (
      (typeof detail === "string" &&
        ((detail.includes("401") && detail.includes("Unauthorized")) ||
          (detail.includes("403") && detail.includes("Forbidden")) ||
          detail.toLowerCase().includes("token expired") ||
          detail.toLowerCase().includes("invalid token") ||
          detail.toLowerCase().includes("authentication failed"))) ||
      (typeof message === "string" &&
        ((message.includes("401") && message.includes("Unauthorized")) ||
          (message.includes("403") && message.includes("Forbidden")) ||
          message.toLowerCase().includes("token expired") ||
          message.toLowerCase().includes("invalid token") ||
          message.toLowerCase().includes("authentication failed")))
    );
  };

  const handleAuthError = (
    error: any,
    defaultMessage: string,
    errorAction?: any,
  ) => {
    if (isJiraAuthError(error)) {
      const msg =
        "Please check your Jira integration credentials. Your token may have expired or is invalid.";
      toast.error(msg);
      if (errorAction) {
        dispatch(errorAction(msg));
      }
    } else {
      showError(error);
      if (errorAction) {
        dispatch(errorAction(error?.response?.data?.message || defaultMessage));
      }
    }
  };

  const fetchJiraProjects = async (): Promise<ProjectOption[]> => {
    try {
      const response = await axios.get<ProjectsResponse>(
        "/integration/jira/projects",
      );

      if (response.data && response.data.projects) {
        return response.data.projects.map((project) => ({
          value: project.project_id,
          label: project.project_name,
        }));
      }
      return [];
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const fetchJiraEpics = async (
    projectName: string,
    projectId: string,
  ): Promise<{
    epics: EpicOption[];
    boards: BoardOption[];
    statusStages?: StatusStages;
    statusStagesOrder?: StatusStagesOrder;
    displayMessage?: string;
  }> => {
    try {
      const response = await axios.post<EpicsResponse>(
        `/integration/jira/epics`,
        {
          project_name: projectName,
          project_id: projectId,
        },
      );

      let epics: EpicOption[] = [];
      let boards: BoardOption[] = [];
      if (response.data && response.data.epics) {
        epics = response.data.epics.map((epic) => ({
          value: epic.epic_id,
          label: epic.epic_name,
          isImported: (epic as any).is_imported || false,
          isDisabled: (epic as any).is_imported || false,
          id: epic.id,
        }));

        boards = response.data.boards.map((board) => ({
          value: board.name,
          label: board.name,
        }));
      }

      return {
        epics,
        boards,
        statusStages: response.data.status_stages,
        statusStagesOrder: response.data.status_stages_order,
        displayMessage: response.data.display_message,
      };
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const importJiraEpic = async (
    epicIds: string[],
    projectName: string,
    projectId: string,
    activeBoard: string,
    statusStages?: any,
  ): Promise<ImportEpicResponse> => {
    try {
      // Always include status_stages, even if it's an empty object
      const payload: any = {
        epic_ids: epicIds,
        project_name: projectName,
        project_id: projectId,
        level: 2,
        active_board: activeBoard,
        status_stages: statusStages || {},
      };

      const response = await axios.post<ImportEpicResponse>(
        "/integration/jira/import_jira",
        payload,
      );
      toast.success(response.data.message || "Import successful");
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  // Add this function to the useAzureService hook
  const updateJiraStages = async (
    statusStages: Record<string, Record<string, Record<string, string>>>,
  ): Promise<UpdateStagesResponse> => {
    try {
      const payload: UpdateStagesRequest = {
        status_stages: statusStages,
      };

      const response = await axios.post<UpdateStagesResponse>(
        "/integration/jira/update_stages",
        payload,
      );
      toast.success(
        response.data.message || "Status stages updated successfully",
      );
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  // Add this function to the useJiraService hook
  const fetchJiraSettings = async (
    existingData?: any,
  ): Promise<{
    parsedStages: Record<string, any>;
    parsedStagesOrder?: Record<string, any>;
    processedProjects?: Record<string, { hidden: boolean }>;
    priorities?: Record<string, any>;
  }> => {
    try {
      const responseData =
        existingData ||
        (await axios.get<JiraSettingsResponse>("/integration/jira_settings"))
          .data;

      let parsedStages = {};
      let parsedStagesOrder = {};
      let processedProjects: Record<string, { hidden: boolean }> = {};

      if (responseData && responseData.status_stages) {
        // Check if it's a string (for backward compatibility)
        if (typeof responseData.status_stages === "string") {
          console.error(
            "Received status_stages as a string, but expected an object",
          );
          try {
            // Try to parse it anyway
            parsedStages = JSON.parse(responseData.status_stages);
          } catch (error) {
            console.error("Error parsing status_stages string:", error);
            parsedStages = {};
          }
        } else {
          parsedStages = responseData.status_stages;
        }
      }

      // Handle status_stages_order
      if (responseData && responseData.status_stages_order) {
        if (typeof responseData.status_stages_order === "string") {
          try {
            parsedStagesOrder = JSON.parse(responseData.status_stages_order);
          } catch (error) {
            console.error("Error parsing status_stages_order string:", error);
            parsedStagesOrder = {};
          }
        } else {
          parsedStagesOrder = responseData.status_stages_order;
        }
      }

      // Handle processed_projects
      if (responseData && responseData.processed_projects) {
        processedProjects = responseData.processed_projects;
      }

      return {
        parsedStages,
        parsedStagesOrder,
        processedProjects,
        priorities: responseData?.priorities,
      };
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  // Manage Jira projects visibility (hide/show)
  const manageJiraProjects = async (
    projects: Array<{ name: string; hidden: boolean }>,
  ): Promise<ManageProjectsResponse> => {
    try {
      const payload: ManageProjectsRequest = {
        projects,
      };

      const response = await axios.put<ManageProjectsResponse>(
        "/integration/jira/manage_projects",
        payload,
      );
      toast.success(response.data.message || "Projects updated successfully");
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  // Add function to refresh Jira status stages
  const refreshJiraStatusStages = async (
    check_status_stages: boolean = false,
    suppressToast: boolean = false,
  ): Promise<{
    epics: EpicOption[];
    boards: BoardOption[];
    statusStages?: StatusStages;
    statusStagesOrder?: StatusStagesOrder;
    displayMessage?: string;
    processing_status_stages?: boolean;
  }> => {
    try {
      const response = await axios.post<EpicsResponse>(
        `/integration/jira/refresh_status_stages?check_status_stages=${check_status_stages}`,
      );

      let epics: EpicOption[] = [];
      let boards: BoardOption[] = [];

      if (response.data && response.data.epics) {
        epics = response.data.epics.map((epic) => ({
          value: epic.epic_id,
          label: epic.epic_name,
          isImported: (epic as any).is_imported || false,
          isDisabled: (epic as any).is_imported || false,
        }));

        boards = response.data.boards.map((board) => ({
          value: board.name,
          label: board.name,
        }));
      }

      if (!suppressToast) {
        toast.success(
          response.data.display_message ||
            response.data.message ||
            "Status stages refreshed successfully",
        );
      }

      return {
        epics,
        boards,
        statusStages: response.data.status_stages,
        statusStagesOrder: response.data.status_stages_order,
        displayMessage: response.data.display_message,
        processing_status_stages: response.data.processing_status_stages,
      };
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const fetchJiraUsers = async (): Promise<void> => {
    dispatch(setUsersLoading(true));
    dispatch(setUsersError(null));
    try {
      const response = await axios.get<UsersResponse>(
        "/integration/jira/users",
      );
      dispatch(
        setUsers({
          users: response.data.users,
          summary: response.data.summary,
        }),
      );
    } catch (error: any) {
      handleAuthError(error, "Failed to fetch users", setUsersError);
    } finally {
      dispatch(setUsersLoading(false));
    }
  };

  const importJiraUsers = async (users: User[]): Promise<void> => {
    dispatch(setImportUsersLoading(true));
    dispatch(setImportUsersError(null));
    dispatch(setImportUsersSuccess(false));
    try {
      await axios.post<ImportUsersResponse>("/integration/jira/users", {
        users,
      });
      toast.success("Jira users imported successfully.");
      dispatch(setImportUsersSuccess(true));
    } catch (error: any) {
      handleAuthError(error, "Failed to import users", setImportUsersError);
    } finally {
      dispatch(setImportUsersLoading(false));
    }
  };

  /**
   * Fetches available Jira projects
   */
  const getJiraProjects = async () => {
    try {
      const response = await axios.get("/integration/jira/projects");
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  /**
   * Initiates project export to Jira
   * @param payload - { entity_id, jira_project_key, jira_project_name, active_board, integration_type }
   */
  const exportProject = async (payload: {
    entity_id: string;
    jira_project_key: string;
    jira_project_name: string;
    active_board: string;
    integration_type: string;
  }) => {
    try {
      const response = await axios.post("/integration/export_project", payload);
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  /**
   * Fetches active and available boards for a Jira project
   * @param project_name - The name of the Jira project
   */
  const getJiraActiveBoard = async (project_name: string) => {
    try {
      const response = await axios.get("/integration/jira/active_board", {
        params: { project_name },
      });
      return response.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  /**
   * Gets the current sync status for an integration
   */
  const getSyncStatus = async (integration_type: string, id: string) => {
    try {
      const response = await axios.get(
        `/integration/resync/status/${integration_type}/${id}`,
      );
      return response.data;
    } catch (error) {
      // Silently fail for polling unless specified
      return null;
    }
  };

  
  return {
    fetchJiraProjects,
    fetchJiraEpics,
    importJiraEpic,
    updateJiraStages,
    fetchJiraSettings,
    manageJiraProjects,
    fetchJiraUsers,
    importJiraUsers,
    refreshJiraStatusStages,

    getJiraProjects,
    getJiraActiveBoard,
    exportProject,
    getSyncStatus,
  };
};;

export default useJiraService;
